import { defineStore } from "pinia";
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
} from "@microsoft/signalr";

// If you need to test call locally, you can use the following imports:
//import { HubConnection, HubConnectionBuilder } from "../tests/mocks/signalr.ts";
//import { JackBurtonApi } from "@/tests/mocks/api.ts";
import {
  BlockKitContext,
  CloseContactRequest,
  Contact,
  ContactEventEntity,
  ContactStatusType,
  CreateSearchRequest,
  CreateSuggestionRequest,
  CustomerEntry,
  CustomerPreview,
  Field,
  FieldType,
  GetCustomerPreviewResponse,
  Guidance,
  GuidanceFromJSON,
  JackBurtonApi,
  PartialUtterance,
  ProductRecommendation,
  Search,
  SearchResult,
  SearchResultDisplayType,
  Suggestion,
  TaskSuggestion,
  Utterance,
  Value,
} from "@/services/api/sdk";
import { useAuthStore } from "@/store/auth.ts";
import { AuthenticationService } from "@/services/authentication.ts";
import { ApiConfiguration } from "@/services/api/api-configuration.ts";
import {
  ISuggestionSearchStatus,
  useCallSuggestionsStore,
} from "@/store/contact/call-suggestions.ts";

import {
  buildCallEventsTimeline,
  CallEventType,
  ContactEvent,
  ContactParticipationEvent,
  ContactTimelineData,
  ContactTimelineEvent,
  getCurrentCallStatus,
  ICallStatus,
} from "@/utils/contacts/contact";
import { PARTIAL_UTTERANCE_ID } from "@/utils/contacts/utterance";
import { useCallUtteranceStore } from "@/store/contact/call-utterances";
import { useCallGuidanceStore } from "@/store/contact/call-guidance";
import { useCustomerEntryStore } from "@/store/contact/customer-entry";
import { createWS, useConnection } from "@/services/ws";
import { useCallProductRecommendationsStore } from "@/store/contact/call-product-recommendations";
import { Disposable } from "@/utils/disposable";
import { useClientConfig } from "@/store/client-config";
import { useTaskSuggestionStore } from "@/store/contact/task-suggestion";

export interface ISuggestion {
  id: number;
  value: Value;
  groupName: string;
  sources: {
    confidence: number;
  }[];
}

export enum LiveCallMode {
  DEMO = "DEMO",
  LIVE = "LIVE",
}

export enum ICallView {
  CALL_PROGRESS = "CALL_PROGRESS",
  LIVE_TRANSCRIPT = "LIVE_TRANSCRIPT",
}

export enum IAgentStatusType {
  AVAILABLE = "AVAILABLE",
  BUSY = "BUSY",
  OFFLINE = "OFFLINE",
}

export interface IAgentStatus {
  statusId: string;
  type: IAgentStatusType;
  label: string;
}

export enum LiveCallStatus {
  IDLE = "IDLE",
  LOADING = "LOADING",
  LISTENING = "LISTENING",
  WAITING = "WAITING",
  ENDED = "ENDED",
}

export interface CallStream {
  isConnected: boolean;
  callStatus: ICallStatus;
  incomingNumber: string;
  contactName: string;
  isMuted: boolean;
  type: "INBOUND" | "OUTBOUND";
  agentStatus: IAgentStatus["statusId"] | null;
  agentStatuses: IAgentStatus[];
}

export interface ICallPreferences {
  view: ICallView;
}

function assert<T>(val: T | null): asserts val is T {
  if (val === null) {
    throw new Error("Assertion failed");
  }
}

function createEmptyCallStream(): CallStream {
  return {
    agentStatus: null,
    agentStatuses: [],
    isConnected: false,
    callStatus: ICallStatus.IDLE,
    isMuted: false,
    type: "INBOUND",
    incomingNumber: "",
    contactName: "",
  };
}

type EventId<T extends ContactTimelineEvent['type']> = `${T}_${string}`;
type AnyEventId = EventId<ContactTimelineEvent['type']>;

interface ILiveCallStore {
  unclosedContact: Contact | null;
  state: LiveCallStatus;
  callListeners: Disposable | null;
  mode: LiveCallMode;
  stream: CallStream;
  activeCall: Contact | null;
  preferences: ICallPreferences;
  suggestionFields: Field[];
  events: Record<AnyEventId, ContactTimelineEvent>;
  contactEvents: ContactEventEntity[];
  transcriptSummary: string | null;
}

export const useLiveCallStore = defineStore("live-call-events", {
  state: (): ILiveCallStore => {
    return {
      state: LiveCallStatus.IDLE,
      callListeners: null,
      mode: LiveCallMode.LIVE,
      stream: createEmptyCallStream(),
      activeCall: null,
      preferences: { view: ICallView.CALL_PROGRESS },
      suggestionFields: [],
      events: {},
      contactEvents: [],
      unclosedContact: null,
      transcriptSummary: null,
    };
  },

  actions: {
    async initiate(): Promise<void> {
      await this.listenForCallCreation();
    },

    async startConnection(): Promise<void> {
      const { getConnection } = useConnection();
      await getConnection();
    },

    async closeConnection(): Promise<void> {
      const { closeConnection } = useConnection();
      await closeConnection();
    },

    async useWS() {
      const { getConnection } = useConnection();
      const connection = await getConnection();

      return createWS(connection);
    },

    async loadActiveCall(contactId: number): Promise<void> {
      const api: JackBurtonApi = new JackBurtonApi(ApiConfiguration);
      if (this.state === LiveCallStatus.WAITING) {
        this.state = LiveCallStatus.LOADING;
        const call: Contact = await api.getContact({ contactId });
        const status = getCurrentCallStatus(call);

        if (status && status === ContactStatusType.Started) {
          this.state = LiveCallStatus.LISTENING;
          this.setActiveCall(call);

          if (this.activeCall!.participations?.length) {
            this.addEvent(
              CallEventType.PARTICIPATION,
              this.activeCall!.participations[0].dateTransferred!,
              this.activeCall!.participations[0],
            );
          }

          await this.loadCallData();
          await this.listenForCallEvents();
        } else {
          this.state = LiveCallStatus.WAITING;
        }
      }
    },

    async endCall(): Promise<void> {
      assert(this.activeCall);

      const callSuggestionsStore = useCallSuggestionsStore();
      const callGuidanceStore = useCallGuidanceStore();
      const callUtteranceStore = useCallUtteranceStore();
      const customerEntryStore = useCustomerEntryStore();
      const productRecommendationsStore = useCallProductRecommendationsStore();
      const taskSuggestionStore = useTaskSuggestionStore();

      await this.unsubscribeFromCallEvents();
      this.activeCall = null;
      this.unclosedContact = null;
      this.events = {};
      this.state = LiveCallStatus.WAITING;
      this.mode = LiveCallMode.LIVE;
      this.suggestionFields = [];
      this.contactEvents = [];
      this.updateCallStream({ callStatus: ICallStatus.ENDED });
      callSuggestionsStore.$reset();
      callUtteranceStore.$reset();
      callGuidanceStore.$reset();
      customerEntryStore.$reset();
      productRecommendationsStore.$reset();
      taskSuggestionStore.$reset();
    },

    setActiveCall(call: Contact): void {
      this.activeCall = call;
    },

    async fetchContactBySourceId(sourceId: string): Promise<void> {
      const api: JackBurtonApi = new JackBurtonApi(ApiConfiguration);
      const contact: Contact = await api.getContactBySourceId({
        sourceId,
      });

      const status = getCurrentCallStatus(contact);
      if (status === ContactStatusType.Ended) {
        this.unclosedContact = contact;
      } else {
        this.setActiveCall(contact);
        await this.fetchContactPreview(contact.id!);
        await this.fetchGuidanceForContact(contact.id!);
      }
    },

    async openEndedCall() {
      if (!this.unclosedContact) return;

      this.setActiveCall(this.unclosedContact);
      await this.loadCallData();
    },

    async loadCallData(): Promise<void> {
      await this.loadUtterances();
      await this.loadGuidance();
      await this.loadSuggestions();
      await this.loadContactEvents();
      await this.fetchCallFields();
    },

    async loadUtterances(): Promise<void> {
      const api: JackBurtonApi = new JackBurtonApi(ApiConfiguration);
      assert(this.activeCall);
      const utterances: Utterance[] = await api.getContactUtterances({
        contactId: this.activeCall.id!,
      });
      utterances.forEach(this.addUtterance);
    },
    async loadContactEvents(): Promise<void> {
      const api: JackBurtonApi = new JackBurtonApi(ApiConfiguration);
      assert(this.activeCall);
      const evts: ContactEventEntity[] = await api.getContactEvents({
        contactId: this.activeCall.id!,
      });
      evts.forEach(this.addContactEvent);
    },
    async loadCustomerEntries(): Promise<void> {
      const api: JackBurtonApi = new JackBurtonApi(ApiConfiguration);
      assert(this.activeCall);
      const res = await api.getCustomerEntries({
        customerId: this.activeCall.customerId!,
      });
      const store = useCustomerEntryStore();
      store.$reset();
      res.data!.forEach(this.addCustomerEntry);
    },

    async loadSuggestions(): Promise<void> {
      const api: JackBurtonApi = new JackBurtonApi(ApiConfiguration);
      assert(this.activeCall);
      const suggestions: Suggestion[] = await api.getContactSuggestions({
        contactId: this.activeCall.id!,
      });
      suggestions.forEach(this.addSuggestion);
    },

    async loadGuidance(): Promise<void> {
      assert(this.activeCall);
      await this.fetchGuidanceForContact(this.activeCall.id!);
    },

    async fetchCallFields(): Promise<void> {
      const api: JackBurtonApi = new JackBurtonApi(ApiConfiguration);
      assert(this.activeCall);

      const fields = await api.getContactFields({
        contactId: this.activeCall.id!,
      });
      this.suggestionFields = fields.filter((field: Field) => !field.isHidden);
    },

    async listenForCallCreation(): Promise<void> {
      this.state = LiveCallStatus.WAITING;

      const ws = await this.useWS();

      const handler = ws.onContactCreated(async (call) => {
        if (this.state === LiveCallStatus.WAITING) {
          const api: JackBurtonApi = new JackBurtonApi(ApiConfiguration);
          const contact = await api.getContact({
            contactId: call.contactId!,
          });

          this.setActiveCall(contact);
          this.state = LiveCallStatus.LISTENING;
          this.addEvent(
            CallEventType.PARTICIPATION,
            this.activeCall!.participations![0].dateTransferred!,
            this.activeCall!.participations![0],
          );

          await this.loadCallData();
          await this.listenForCallEvents();
        }
      });

      if (!handler) {
        return;
      }

      const { listen } = handler;

      await listen();
    },

    async listenForCallEvents(): Promise<void> {
      assert(this.activeCall);
      const callId = this.activeCall.id!;

      const ws = await this.useWS();
      this.callListeners = await ws.setupListeners(() => {
        ws.onContactUpdated(callId, this.updateActiveCall);
        ws.onUtteranceCreated(callId, this.addUtterance);
        ws.onUtteranceUpdated(callId, this.updateUtterance);
        ws.onPartialUtteranceCreated(callId, this.addPartialUtterance);
        ws.onSuggestionCreated(callId, this.addSuggestion);
        ws.onSuggestionUpdated(callId, this.updateSuggestion);
        ws.onSearchCreated(callId, this.processSearchCreation);
        ws.onCustomerEntryCreated(
          this.activeCall!.customerId!,
          this.addCustomerEntry,
        );
        ws.onGuidanceCreated(callId, this.addGuidance);
        ws.onProductRecommendationCreated(
          callId,
          this.updateProductRecommendation,
        );
        ws.onContactSummaryCreated(callId, this.updateTranscriptSummary);
        ws.onContactEventCreated(callId, this.addContactEvent);
        ws.onTaskSuggestionCreated(callId, this.addTaskSuggestion);
      });
    },

    async updateActiveCall(contactId: number): Promise<void> {
      const api: JackBurtonApi = new JackBurtonApi(ApiConfiguration);
      const call = await api.getContact({
        contactId,
      });

      this.setActiveCall(call);

      const status = getCurrentCallStatus(call);
      if (status && status === ContactStatusType.Closed) {
        await this.endCall();
      }
      await this.loadCustomerEntries();
    },

    async unsubscribeFromCallEvents(): Promise<void> {
      this.callListeners?.dispose();
    },

    updateCallStream(data: Partial<CallStream>): void {
      this.stream = {
        ...(this.stream || ({} as CallStream)),
        ...data,
      };
    },

    resetCallStream(): void {
      this.stream = {
        ...createEmptyCallStream(),
        isConnected: this.stream?.isConnected,
        agentStatus: this.stream?.agentStatus,
        agentStatuses: this.stream?.agentStatuses,
      };
    },

    async fetchContactPreview(contactId: number): Promise<void> {
      const api: JackBurtonApi = new JackBurtonApi(ApiConfiguration);
      const { data }: GetCustomerPreviewResponse = await api.getCustomerPreview(
        {
          contactId,
        },
      );

      const contactName = data!.matched
        ? `${data!.firstName} ${data!.lastName}`
        : "Unknown";
      this.updateCallStream({
        contactName,
      });
    },

    async fetchGuidanceForContact(contactId: number): Promise<void> {
      const api: JackBurtonApi = new JackBurtonApi(ApiConfiguration);
      const guidance: Guidance[] = await api.getContactGuidance({
        contactId,
      });

      guidance.forEach(this.addGuidance);
    },

    addSuggestion(suggestion: Suggestion): void {
      const callSuggestionsStore = useCallSuggestionsStore();
      callSuggestionsStore.addSuggestion(suggestion);
    },
    addContactEvent(event: ContactEventEntity): void {
      if (event.eventIsHidden) return;
      this.contactEvents.push(event);
      this.addEvent(CallEventType.EVENT, new Date(event.dateCreated!), event);
    },
    addTaskSuggestion(suggestion: TaskSuggestion): void {
      const taskSuggestionStore = useTaskSuggestionStore();
      taskSuggestionStore.addSuggestion(suggestion);
    },
    addGuidance(data: any): void {
      var guidance = GuidanceFromJSON(data);
      const callGuidanceStore = useCallGuidanceStore();
      callGuidanceStore.addGuidance(guidance);
    },

    async updateSuggestion(_suggestion: Suggestion): Promise<void> {
      // const _callSuggestionsStore = useCallSuggestionsStore();
      // await callSuggestionsStore.updateSuggestionStatus({
      //     suggestion,
      //     status: SuggestionStatus.Accepted,
      // });
    },

    async updateTranscriptSummary(contactId: number): Promise<void> {
      const api: JackBurtonApi = new JackBurtonApi(ApiConfiguration);
      const response = await api.getContactTranscriptSummary({ contactId });

      this.transcriptSummary = response?.data?.text || null;
    },

    async createSuggestion(
      createSuggestionRequest: CreateSuggestionRequest,
    ): Promise<void> {
      assert(this.activeCall);
      const api: JackBurtonApi = new JackBurtonApi(ApiConfiguration);
      await api.createSuggestion({
        contactId: this.activeCall.id!,
        createSuggestionRequest,
      });
    },

    addEvent(
      type: ContactTimelineEvent["type"],
      date: Date,
      event: ContactTimelineData,
    ): void {
      const participations = Object.keys(this.events).filter((key) => {
        return this.events[key as AnyEventId].type === CallEventType.PARTICIPATION;
      }) as EventId<CallEventType.PARTICIPATION>[];

      // TODO: Whatever needs this could infer it from the date of the events and the participations
      //       computed values should make it easy to get the aget that was on the call at a scpecific time
      const lastParticipation = this.events[participations[participations.length - 1]] as ContactParticipationEvent;

      const agentMeta = lastParticipation ? { id: lastParticipation.agentId!, firstName: lastParticipation.firstName!, lastName: lastParticipation.lastName! } : undefined

      this.events[`${type}_${event.id!}`] = {
        type,
        date,
        ...(agentMeta ? { agent: agentMeta } : {}),
        ...event,
      };
    },

    addUtterance(utterance: Utterance): void {
      this.removePartialUtterance();
      this.addEvent(
        CallEventType.UTTERANCE,
        new Date(utterance.dateStarted!),
        utterance,
      );
    },

    updateUtterance(utterance: Utterance): void {
      // Update the event in the timeline, but keep its original key
      this.addEvent(
        CallEventType.UTTERANCE,
        new Date(utterance.dateStarted!),
        utterance,
      );
    },

    addPartialUtterance(evt: PartialUtterance): void {
      this.addEvent(
        CallEventType.UTTERANCE,
        new Date(evt.utterance!.dateStarted!),
        evt.utterance!,
      );
    },

    removePartialUtterance(): void {
      delete this.events[`${CallEventType.UTTERANCE}_${PARTIAL_UTTERANCE_ID}`];
    },
    async removeContactEvent(id: number): Promise<void> {
      const api: JackBurtonApi = new JackBurtonApi(ApiConfiguration);
      assert(this.activeCall);
      try {
        await api.deleteContactEvent({
          eventId: id,
          contactId: this.activeCall!.id!,
        });
        delete this.events[`${CallEventType.EVENT}_${id}`];
        this.contactEvents = this.contactEvents.filter(
          (event) => event.id !== id,
        );
      } catch (e) {
        console.error(e);
      }
    },

    async createSuggestionSearch(
      createSearchRequest: CreateSearchRequest,
    ): Promise<void> {
      assert(this.activeCall);
      const api: JackBurtonApi = new JackBurtonApi(ApiConfiguration);
      await api.createSearch({
        contactId: this.activeCall.id!,
        createSearchRequest,
      });
    },

    async processSearchCreation(searchRequest: Search): Promise<void> {
      const callSuggestionStore = useCallSuggestionsStore();
      callSuggestionStore.addSearch(searchRequest);

      const ws = await this.useWS();

      const createdListener = ws.onSearchResultCreated(
        searchRequest.id!,
        async (e) => {
          this.processSearchResult(searchRequest, e);
          window.clearTimeout(timer);
          createdListener?.dispose();

          const acceptedListener = ws.onSearchResultAccepted(
            searchRequest.id!,
            () => {
              this.removeSearchResult(searchRequest);
              // Stop listening to those events once we got a result
              acceptedListener?.dispose();
            },
          );

          await acceptedListener?.listen();
        },
      );

      const timer = window.setTimeout(() => {
        // Stop waiting after 10sec have passed
        // TODO: maybe display an error message on timeout
        createdListener?.dispose();
      }, 10000);

      await createdListener?.listen();
    },

    async triggerSuggestionSearch(search: Search): Promise<void> {
      const callSuggestionStore = useCallSuggestionsStore();
      callSuggestionStore.setSearchStatus(
        search.id!.toString(),
        ISuggestionSearchStatus.SEARCHING,
      );
    },

    async processSearchResult(
      search: Search,
      result: SearchResult,
    ): Promise<void> {
      assert(this.activeCall);

      const callSuggestionStore = useCallSuggestionsStore();
      callSuggestionStore.completeSearchRequest(search, result);

      const suggestionField = this.suggestionFields.find(
        (field) => field.id === search.fieldId,
      );

      if (!suggestionField) {
        return;
      }

      if (
        suggestionField.type === FieldType.Search &&
        suggestionField.searchResultDisplayType ===
        SearchResultDisplayType.Inline
      ) {
        if (result.items && result.items.length === 1) {
          const customerEntryStore = useCustomerEntryStore();
          await customerEntryStore.createEntry({
            customerId: this.activeCall.customerId!,
            createContactEntryRequest: {
              searchResultId: result.items[0].id,
              fieldId: suggestionField.id,
              contactId: search.contactId!,
            },
          });
        }
      }
    },

    async removeSearchResult(searchRequest: Search): Promise<void> {
      const callSuggestionStore = useCallSuggestionsStore();
      callSuggestionStore.cleanupSearchForSearchId(searchRequest);
      callSuggestionStore.cleanupSearchResultsForSearchId(searchRequest);
    },

    addCustomerEntry(entry: CustomerEntry): void {
      const customerEntryStore = useCustomerEntryStore();
      customerEntryStore.addEntry(entry);
    },

    updateProductRecommendation(recommendation: ProductRecommendation): void {
      const { queueProducts } = useCallProductRecommendationsStore();
      queueProducts(recommendation.products!);
    },

    async toggleScriptStep(data: {
      chapterId: number;
      completion: boolean;
    }): Promise<void> {
      const api: JackBurtonApi = new JackBurtonApi(ApiConfiguration);
      assert(this.activeCall);
      const call = this.activeCall!;
      if (data.completion) {
        await api.createChapterCompletion({
          contactId: call.id!,
          chapterId: data.chapterId,
        });
      } else {
        await api.deleteChapterCompletion({
          contactId: call.id!,
          chapterId: data.chapterId,
        });
      }

      const { agent } = useAuthStore();

      call.script!.chapters![data.chapterId].completion = data.completion
        ? {
          dateCompleted: new Date(Date.now()),
          userId: agent!.id!,
        }
        : undefined;
    },

    async closeActiveContact(
      closeContactRequest: CloseContactRequest,
    ): Promise<void> {
      assert(this.activeCall);
      const api: JackBurtonApi = new JackBurtonApi(ApiConfiguration);
      await api.closeContact({
        contactId: this.activeCall!.id!,
        closeContactRequest,
      });
    },

    setMode(newMode: LiveCallMode): void {
      this.mode = newMode;
    },
    async executeCustomCommand(
      commandId: string,
      data: Record<string, string>,
      context: BlockKitContext,
    ): Promise<void> {
      assert(this.activeCall);
      const api: JackBurtonApi = new JackBurtonApi(ApiConfiguration);
      await api.createCommandExecution({
        contactId: this.activeCall.id!,
        createCommandExecutionRequest: {
          commandId,
          data,
          context,
        },
      });
    },
    async contactCustomerAssignment(
      callId: number,
      customer: CustomerPreview | null,
    ) {
      const api = new JackBurtonApi(ApiConfiguration);
      await api.createContactCustomerAssignment({
        contactId: callId,
        assignCustomerToContactRequest: {
          customerId: customer?.customerId,
        },
      });
    },
  },

  getters: {
    contactId(state): number | null {
      return state.activeCall?.id || null;
    },

    utteranceCount(state): number {
      return Object.keys(state.events).reduce(
        (utterances: number, eventId: string) => {
          const event = this.events[`${CallEventType.UTTERANCE}_${eventId}`];
          if (event?.type === CallEventType.UTTERANCE) {
            return utterances + 1;
          }

          return utterances;
        },
        0,
      );
    },

    timeline(state): ContactTimelineEvent[] {
      if (!state.activeCall) return [];

      const filteredEvents = Object.values(state.events).filter(
        (event: ContactTimelineEvent, index: number) => {
          const isPartialUtterance =
            event.type === CallEventType.UTTERANCE && !!event.partialId;
          const isLastEvent = index + 1 === Object.values(state.events).length;
          return !(!isLastEvent && isPartialUtterance);
        },
      );

      return buildCallEventsTimeline(filteredEvents);
    },

    isConnectedToStream(state): boolean {
      return state.stream?.isConnected || false;
    },

    hasIncomingCall(state): boolean {
      return (
        state.stream?.callStatus === ICallStatus.INCOMING &&
        state.stream?.type === "INBOUND"
      );
    },

    onActiveCall(state): boolean {
      return state.stream?.callStatus === ICallStatus.ACTIVE;
    },

    currentAgentStatus(state): IAgentStatus | null {
      const matchingStatus = state.stream?.agentStatuses?.find(
        (status) => state.stream.agentStatus === status.statusId,
      );
      return matchingStatus || null;
    },

    activeSuggestionFields(state): Field[] {
      const { entriesByFieldId } = useCustomerEntryStore();

      return state.suggestionFields.filter((field: Field) => {
        const isSingleCaptureField = field.type !== FieldType.Search;
        const isSimpleSearchField =
          field.type === FieldType.Search &&
          field.searchResultDisplayType === SearchResultDisplayType.Inline;
        const hasCapturedField = Object.keys(entriesByFieldId)
          .map((key) => parseInt(key, 10))
          .includes(field.id!);

        return !(
          (isSingleCaptureField || isSimpleSearchField) &&
          hasCapturedField
        );
      });
    },

    showLocalCustomerTime(state): boolean {
      return !!(state.activeCall?.timezone && state.activeCall?.locale);
    },

    hasActiveCallEnded(state): boolean {
      if (!state.activeCall) return false;

      const status = getCurrentCallStatus(state.activeCall);
      return status === ContactStatusType.Ended;
    },

    hasActiveCallClosed(state): boolean {
      if (!state.activeCall) return false;

      const status = getCurrentCallStatus(state.activeCall);
      return status === ContactStatusType.Closed;
    },
  },
});
